import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  CubeIcon,
  TrophyIcon,
  UsersIcon,
  CurrencyDollarIcon,
  BellIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { reportsAPI, batchesAPI, drawsAPI, systemAPI } from '../services/api';
import { StatCard, LoadingSpinner, EmptyState, Badge } from '../components/common';
import { formatCurrency, formatDate, formatRelativeTime } from '../utils/formatters';
import useAuthStore from '../store/authStore';

export default function DashboardPage() {
  const user = useAuthStore((state) => state.user);

  // Fetch system summary
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: async () => {
      const response = await reportsAPI.getSummary();
      return response.data;
    },
  });  

  // Fetch system stats
  const { data: systemStats } = useQuery({
    queryKey: ['system-stats'],
    queryFn: async () => {
      const response = await systemAPI.getStats();
      return response.data;
    },
    enabled: user?.role === 'admin' || user?.role === 'staff',
  });

  // Fetch recent batches
  const { data: batches } = useQuery({
    queryKey: ['dashboard-batches'],
    queryFn: async () => {
      const response = await batchesAPI.getAll({ limit: 5 });
      return response.data;
    },
  });

  // Fetch live draws with auto-refresh
  const { data: liveDraws } = useQuery({
    queryKey: ['dashboard-live-draws'],
    queryFn: async () => {
      const response = await drawsAPI.getLive();
      return response.data;
    },
    refetchInterval: 5000, // Refresh every 5 seconds
  });

  if (summaryLoading) {
    return <LoadingSpinner text="Loading dashboard..." />;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-sm text-gray-600">
          Welcome back! Here's what's happening with your lottery system today.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Link to="/batches">
          <StatCard
            title="Total Batches"
            value={batches?.data?.length || 0}
            icon={CubeIcon}
            color="primary"
          />
        </Link>

        <Link to="/draws">
          <StatCard
            title="Active Draws"
            value={liveDraws?.data?.length || 0}
            icon={TrophyIcon}
            color="green"
          />
        </Link>

        <Link to="/members">
          <StatCard
            title="Total Members"
            value={summary?.summary?.total_members || 0}
            icon={UsersIcon}
            color="blue"
          />
        </Link>

        <Link to="/reports">
          <StatCard
            title="Total Revenue"
            value={formatCurrency(summary?.summary?.total_deposits_cents || 0)}
            icon={CurrencyDollarIcon}
            color="green"
          />
        </Link>
      </div>

      {/* Additional Stats Row */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Winners"
          value={summary?.summary?.total_winners || 0}
          icon={TrophyIcon}
          color="yellow"
        />

        <StatCard
          title="Pending Deposits"
          value={formatCurrency(summary?.summary?.pending_deposits_cents || 0)}
          icon={CurrencyDollarIcon}
          color="yellow"
        />

        <StatCard
          title="Service Fees"
          value={formatCurrency(summary?.summary?.total_service_fees_cents || 0)}
          icon={ChartBarIcon}
          color="blue"
        />

        <StatCard
          title="Notifications Sent"
          value={systemStats?.notifications_sent_today || 0}
          icon={BellIcon}
          color="primary"
        />
      </div>

      {/* Recent Activity Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Batches */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Recent Batches</h2>
            <Link to="/batches" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              View all →
            </Link>
          </div>
          {batches?.data && batches.data.length > 0 ? (
            <div className="space-y-3">
              {batches.data.slice(0, 5).map((batch) => (
                <Link
                  key={batch.id}
                  to={`/batches/${batch.id}`}
                  className="block p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-gray-900">
                      {batch.name || `Batch ${batch.batch_number}`}
                    </p>
                    <Badge variant={
                      batch.status === 'active' ? 'success' :
                      batch.status === 'open' ? 'info' :
                      batch.status === 'full' ? 'warning' :
                      'default'
                    }>
                      {batch.status}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">
                      {batch.current_members || 0} / {batch.total_slots} members
                    </span>
                    <span className="text-gray-500">
                      {formatDate(batch.created_at)}
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full transition-all"
                      style={{
                        width: `${Math.min(((batch.current_members || 0) / batch.total_slots) * 100, 100)}%`
                      }}
                    />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={CubeIcon}
              title="No batches yet"
              description="Create your first batch to get started"
            />
          )}
        </div>

        {/* Live Draws */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Live Draws</h2>
            <Link to="/draws" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              View all →
            </Link>
          </div>
          {liveDraws?.data && liveDraws.data.length > 0 ? (
            <div className="space-y-3">
              {liveDraws.data.map((draw) => (
                <Link
                  key={draw.id}
                  to={`/draws`}
                  className="block p-4 bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 rounded-lg border border-green-200 transition-all"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium text-gray-900">Draw #{draw.id}</p>
                      <p className="text-sm text-gray-600">
                        Batch #{draw.batch_id}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                      </span>
                      <Badge variant="success">Live</Badge>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">
                      {draw.total_candidates || 0} candidates
                    </span>
                    <span className="text-gray-600">
                      {draw.winners_selected || 0} / {draw.total_winners || 0} winners
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={TrophyIcon}
              title="No active draws"
              description="No draws are currently in progress"
            />
          )}
        </div>
      </div>

      {/* Payment Status Overview (Admin only) */}
      {/* {user?.role === 'admin' && summary && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Payment Status Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">{summary.payment_status_green || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Green Status</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-3xl font-bold text-yellow-600">{summary.payment_status_yellow || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Yellow Status</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-3xl font-bold text-red-600">{summary.payment_status_red || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Red Status</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-600">{summary.payment_status_defaulted || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Defaulted</p>
            </div>
          </div>
        </div>
      )} */}

      {/* System Health (Staff/Admin only) */}
      {(user?.role === 'admin' || user?.role === 'staff') && systemStats && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">System Health</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Uptime</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                {Math.floor((systemStats?.stats?.process?.uptime_seconds || 0) / 3600)}h
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">API Requests Today</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                {systemStats.requests_today || 0}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Active Users</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                {systemStats.active_users || 0}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Database Size</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                {systemStats?.stats?.database?.size || 'N/A'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

